import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import axiosHttp from "../../../../../services/interceptor";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";

const FeuCarbonCaptureandStorage = forwardRef(({ B33CarbonCaptureTechnologies, refetchMonitoringData,emissionSourceTypes, shouldRefetch,vesselMstId }, ref) => {

	const [emissionSourceOptions, setEmissionSourceOptions] = useState([]);
	const [activeAction, setactiveAction] = useState(-1);
	const [fuelData, setFuelData] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [deletingRowIndex, setDeletingRowIndex] = useState();
	const RowActions = {
		Edit: 0,
		Add: 1,
	};

	useEffect(() => {
		if (Array.isArray(B33CarbonCaptureTechnologies)) {			
			 setFuelData(B33CarbonCaptureTechnologies);
		} else {
			setFuelData([]); // Fallback to empty array
		}
	}, [B33CarbonCaptureTechnologies]);

	const handleDeleteBtnClick = (rowIndex) => {
		setConfirmDelete(true);
		setDeletingRowIndex(rowIndex);
	};

	const getEmissionSourceNames = async () => {
		try {
			const response = await callApi({ url: API_ROUTES.GET_EMISSION_SOURCE_NAMES(vesselMstId), method: "GET", data: {} });
			if (response) {
				const options = response.map((item) => ({
					label: item.value,
					value: item.code,
				}));
				console.log("Emission source options:", options);
				setEmissionSourceOptions(options);
			}
		} catch (error) {
		}
	};

	const [hasFetched, setHasFetched] = useState(false);

	useEffect(() => {
		if (shouldRefetch && !hasFetched) {
			getEmissionSourceNames();
			setHasFetched(true);
		}
	}, [shouldRefetch]);

	useEffect(() => {
		if (!shouldRefetch) {
			setHasFetched(false);
		}
	}, [shouldRefetch]);

	const handleFuelActions = async (table, row, action) => {
		if (activeAction === -1) {
			if (action === RowActions.Edit) {
				table.setEditingRow(row);
				setactiveAction(action);
			} else if (action === RowActions.Add) {
				table.setCreatingRow(true);
				setactiveAction(action);
			}
			return;
		} else return;
	};

	const resetActiveRow = () => {
		setactiveAction(-1);
		setValidationErrors({});
	};

	const validateRequired = (value) => (typeof value === "string" ? value?.length > 0 : typeof value === "number" ? value >= 0 : false);

	const validateFuel = (row, type, rowObject) => {
		return {
			descriptionOfTechnology: !validateRequired(row.descriptionOfTechnology) ? "Field is required" : "",
			appliedEmissionSourceRef: !validateRequired(row.appliedEmissionSourceRef) ? "Field is required" : "",
		};
	};

	const validation = (value) => {
		return value?.length <= 1000 ? value : value.slice(0, 1000);
	};

	useImperativeHandle(ref, () => ({
		async submitAndCheck() {
			const { creatingRow, editingRow } = table.getState();
			if (!creatingRow && !editingRow) return true;
			const mode = creatingRow ? "add" : "edit";
			const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
			let isDirty = false;
			if (editingRow && editedValues) {
				isDirty = Object.keys(editedValues).some(
					(key) => editedValues[key] !== editingRow.original[key]
				);
			}
			const validationRes = validateFuel(editedValues, mode, {});
			const hasErrors = Object.values(validationRes).some((err) => err);
			if (hasErrors) {
				setValidationErrors(validationRes);
				emitToasterMsg(
					"warning",
					"Validation Error",
					"Please correct the highlighted fields before proceeding."
				);
				return false;
			}
			if (creatingRow) {
				await handleCreateCCSCCU({ values: editedValues, table }, true);
				return true;
			} else if (editingRow && isDirty) {
				await handleSaveCCSCCU({ values: editedValues, table, row: editingRow }, true);
				return true;
			}
			if (!isDirty) {
				setactiveAction(-1)
				table.setEditingRow(null);
				return true;
			}
		}
	}));

	const handleCreateCCSCCU = async ({ values, table }, isAuto = false) => {
		const newValidationErrors = validateFuel(values, "add", {});
		if (Object.values(newValidationErrors).some((error) => error)) {
			setValidationErrors(newValidationErrors);
			return;
		}
		setValidationErrors({});
		const newRow = {
			b6CarbonCaptureTechnologyId: 0,
			descriptionOfTechnology: values.descriptionOfTechnology || "",
			supportingEvidence: values.supportingEvidence || "",
			appliedEmissionSourceRef: values.appliedEmissionSourceRef || "",
		};
		const payload = fuelData?.map((item) => ({
			...item,
			b6CarbonCaptureTechnologyId: 0,
		}));
		const finalPayload = [...payload, newRow];
		try {
			const b6ccsccu = await callApi({
				method: "POST",
				url: API_ROUTES.B33(vesselMstId),
				data: finalPayload,
			});
			if (b6ccsccu?.isSuccess) {
				emitToasterMsg("success", "Table CCS/CCU", isAuto ? "Autosaved successfully" : "Added successfully");
				table.setCreatingRow(null);
				setFuelData(finalPayload);
				setactiveAction(-1);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			} else {
				emitToasterMsg("error", "Table CCS/CCU", b6ccsccu?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table CCS/CCU", error.errorMessage);
		}
	};

	const handleSaveCCSCCU = async ({ values, table, row }, isAuto = false) => {
		const newValidationErrors = validateFuel(values, "edit", {});
		if (Object.values(newValidationErrors).some((error) => error)) {
			setValidationErrors(newValidationErrors);
			return;
		}
		setValidationErrors({});
		const updatedData = fuelData.map((item, index) => {
			if (index === row.index) {
				return {
					...item,
					descriptionOfTechnology: values.descriptionOfTechnology || "",
					supportingEvidence: values.supportingEvidence || "",
					appliedEmissionSourceRef: values.appliedEmissionSourceRef || "",
				};
			}
			return item;
		});
		const finalPayload = updatedData?.map((item) => ({
			...item,
			b6CarbonCaptureTechnologyId: 0,
		}));
		try {
			const b6ccsccu = await callApi({
				method: "POST",
				url: API_ROUTES.B33(vesselMstId),
				data: finalPayload,
			});
			if (b6ccsccu?.isSuccess) {
				emitToasterMsg("success", "Table CCS/CCU", isAuto ? "Autosaved successfully" : "Updated successfully");
				table.setEditingRow(null);
				setactiveAction(-1);
				setFuelData(finalPayload);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			} else {
				emitToasterMsg("error", "Table CCS/CCU", b6ccsccu?.errorMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Table CCS/CCU", error.errorMessage);
		}
	};

	const handleDeleteFuel = async (rowIndex) => {
		const updatedData = fuelData.filter((_, index) => index !== rowIndex);
		const finalPayload = updatedData?.map((item) => ({
			...item,
			b6CarbonCaptureTechnologyId: 0,
		}));
		try {
			const b6ccsccu = await callApi({
				method: "POST",
				url: API_ROUTES.B33(vesselMstId),
				data: finalPayload,
			});
			if (b6ccsccu?.isSuccess) {
				emitToasterMsg("success", "Table CCS/CCU", "Deleted successfully");
				setactiveAction(-1);
				setFuelData(finalPayload);
				setConfirmDelete(false);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			} else {
				emitToasterMsg("error", "Table CCS/CCU", b6ccsccu?.errorMessage);
				setConfirmDelete(false);
			}
		} catch (error) {
			emitToasterMsg("error", "Table CCS/CCU", error.errorMessage);
		}
	};

	const columns = useMemo(
		() => [
			{
				accessorKey: "descriptionOfTechnology",
				header: "Description of the technology in use",
				muiEditTextFieldProps: ({ row }) => ({
					required: true,
					error: !!validationErrors?.descriptionOfTechnology,
					helperText: validationErrors?.descriptionOfTechnology,
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							descriptionOfTechnology: undefined,
						}),
					onChange: (e) => {
						const validatedValue = validation(e.target.value);
						e.target.value = validatedValue;
					},
				}),
			},
			{
				accessorKey: "supportingEvidence",
				header: "Supporting evidence for compliance with the requirements spelled out in Article 12(3a) or Article 12(3b) of Directive 2003/87/EC",
				muiEditTextFieldProps: ({ row }) => ({
					onChange: (e) => {
						const validatedValue = validation(e.target.value);
						e.target.value = validatedValue;
					},
				}),
			},
		{
                accessorKey: "appliedEmissionSourceRef",
                header: "Fuel consumer to which capture and storage and/or carbon capture and utilisation is applied",
                editVariant: "select",
                Cell: ({ cell }) => {
                    const found = emissionSourceOptions.find((opt) => opt.value === cell.getValue());
                    return found?.label ?? cell.getValue();
                },
                editSelectOptions: emissionSourceOptions,
                editSelectProps: {
                    getOptionValue: (opt) => opt.value,
                    getOptionLabel: (opt) => opt.label,
                },
                muiEditTextFieldProps: ({ row, cell }) => ({
                    required: true,
                    value: cell.getValue() ?? "", // ✅ ensures initial value is shown on first edit
                    error: !!validationErrors?.appliedEmissionSourceRef,
                    helperText: validationErrors?.appliedEmissionSourceRef,
                    onFocus: () =>
                        setValidationErrors({
                            ...validationErrors,
                            appliedEmissionSourceRef: undefined,
                        }),
                    onChange: (e) => {
                        const selectedValue = e.target.value;
                        row._valuesCache.appliedEmissionSourceRef = selectedValue; // ✅ keeps MRT cache in sync
                    },
                    SelectProps: {
                        MenuProps: {
                            disableScrollLock: true,
                            PaperProps: {
                                sx: {
                                    maxHeight: 250,
                                    boxShadow: 3,
                                    overflowY: "auto",
                                    "&::-webkit-scrollbar": {
                                        width: "8px",
                                    },
                                    "&::-webkit-scrollbar-thumb": {
                                        backgroundColor: "darkgray",
                                        borderRadius: "3px",
                                    },
                                    "&::-webkit-scrollbar-track": {
                                        backgroundColor: "#f0f0f0",
                                    },
                                },
                            },
                            anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                            },
                            transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                            },
                        },
                    },
                }),
            },
        ],
        [validationErrors,emissionSourceOptions]
    );

	const table = useMaterialReactTable({
		columns,
		data: fuelData || [],
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		enableColumnActions: false,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing: true,
		enablePagination: false,
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": {
					borderRight: "none",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "520px",
			},
		},
		displayColumnDefOptions: {
			"mrt-row-actions": {
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},
		},
		muiTableBodyCellProps: ({ row, cell }) => {
			return {
				sx: {
					borderRight: "1px solid lightgray",
					"&:last-child": {
						borderRight: "none",
					},
					color: "",
				},
			};
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				"&:not(:last-of-type)": {
					borderBottom: "1px solid lightgray",
				},
				verticalAlign: "top",
			},
		}),
		muiTableHeadRowProps: {
			sx: {
				borderBottom: "2px solid lightgray",
			},
		},
		getRowId: (row) => {
			return row?.original?.b6CarbonCaptureTechnologyId || row?.b6CarbonCaptureTechnologyId;
		},
		onCreatingRowSave: handleCreateCCSCCU,
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		onEditingRowSave: handleSaveCCSCCU,
		renderToolbarInternalActions: ({ table }) => <></>,
		renderRowActions: ({ row, table }) => {
			return (
				<Box sx={{ display: "flex", gap: "1rem" }}>
					<Tooltip title="Edit">
						<IconButton style={{ padding: "0px" }} onClick={() => handleFuelActions(table, row, RowActions.Edit)}>
							<EditIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Delete">
						<IconButton style={{ padding: "0px" }} color="error" onClick={() => handleDeleteBtnClick(row?.index, true)}>
							<DeleteIcon />
						</IconButton>
					</Tooltip>
				</Box>
			);
		},
		renderTopToolbarCustomActions: ({ table }) => (
			<div className="mt-1">
				<Button
					startIcon={<Icon icon={ICONS.ADD} />}
					onClick={() => {
						handleFuelActions(table, null, RowActions.Add);
					}}
					disabled={!!table.getState().creatingRow || !!table.getState().editingRow}
				>
					Add CCS/CCU
				</Button>
			</div>
		),
	});

	return (
		<>
			<div className="carbon-capture-table mt-1">
				<MaterialReactTable table={table} />
			</div>
			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => { handleDeleteFuel(deletingRowIndex) }}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this ccs/ccu?</div>
				</Modal>
			</div>
		</>
	);
});

export default FeuCarbonCaptureandStorage;
